/**
 * RestoID — Reset password (set a new password).
 * ---------------------------------------------------------------------------
 * Upstream reset logic kept verbatim (password-policy check, error handlers,
 * reset API, redirect) — only the layout is swapped from SecondaryPageLayout to
 * the RestoID frame. The password fields come from `SetPasswordFields`, our
 * RestoID-shaped stand-in for the upstream `@/containers/SetPassword` container:
 * same react-hook-form logic and same i18n keys, rendered with `Field` because the
 * upstream 44px notched-label control cannot be reached by a color-only theme.
 * Swapped in via `src/App.tsx`.
 */
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { resetPassword } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import { usePromiseConfirmModal } from '@/hooks/use-confirm-modal';
import useErrorHandler, { type ErrorHandlers } from '@/hooks/use-error-handler';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePasswordPolicyChecker from '@/hooks/use-password-policy-checker';
import usePasswordRejectionErrorHandler from '@/hooks/use-password-rejection-handler';
import { usePasswordPolicy } from '@/hooks/use-sie';
import useToast from '@/hooks/use-toast';

import SetPasswordFields from '../components/SetPasswordFields';
import { ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

const ResetPassword = () => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const clearErrorMessage = useCallback(() => {
    setErrorMessage(undefined);
  }, []);
  const { t } = useTranslation();
  const c = useCopy();
  const { setToast } = useToast();
  const navigate = useNavigateWithPreservedSearchParams();
  const { show } = usePromiseConfirmModal();
  const { setForgotPasswordIdentifierInputValue } = useContext(UserInteractionContext);

  const checkPassword = usePasswordPolicyChecker({ setErrorMessage });
  const asyncResetPassword = useApi(resetPassword);
  const handleError = useErrorHandler();

  const passwordRejectionErrorHandler = usePasswordRejectionErrorHandler({ setErrorMessage });

  const errorHandlers: ErrorHandlers = useMemo(
    () => ({
      'session.verification_session_not_found': async (error) => {
        await show({ type: 'alert', ModalContent: error.message, cancelText: 'action.got_it' });
        navigate(-2);
      },
      'user.same_password': (error) => {
        setErrorMessage(error.message);
      },
      ...passwordRejectionErrorHandler,
    }),
    [navigate, passwordRejectionErrorHandler, show]
  );

  const onSubmitHandler = useCallback(
    async (password: string) => {
      const success = await checkPassword(password);

      if (!success) {
        return;
      }

      const [error] = await asyncResetPassword(password);

      if (error) {
        await handleError(error, errorHandlers);
        return;
      }

      setForgotPasswordIdentifierInputValue(undefined);
      setToast(t('description.password_changed'));
      navigate('/sign-in', { replace: true });
    },
    [
      asyncResetPassword,
      checkPassword,
      errorHandlers,
      handleError,
      navigate,
      setForgotPasswordIdentifierInputValue,
      setToast,
      t,
    ]
  );

  const {
    policy: {
      length: { max },
    },
    requirementsDescription,
  } = usePasswordPolicy();

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.new_password' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('description.new_password')}
          sub={requirementsDescription}
        />
        <SetPasswordFields
          autoFocus
          isForgotPassword
          errorMessage={errorMessage}
          maxLength={max}
          clearErrorMessage={clearErrorMessage}
          onSubmit={onSubmitHandler}
        />
      </Stack>
    </FirstScreenLayout>
  );
};

export default ResetPassword;
